<template>
    <Head title="Documents" />

    <TailwindLayout title="Documents">
        <div class="py-6">
            <div class="max-w-5xl mx-auto sm:px-6 lg:px-8 space-y-6">
                <div>
                    <h1 class="text-3xl font-semibold leading-tight tracking-tight text-gray-900 dark:text-white">
                        Documents
                    </h1>
                    <p class="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        Your private documents, plus everything you've shared across your properties.
                    </p>
                </div>

                <!-- General (private) documents -->
                <div class="bg-white rounded-lg shadow overflow-hidden">
                    <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                        <div class="min-w-0">
                            <h3 class="text-base font-semibold text-gray-800">General Documents</h3>
                            <p class="text-xs text-gray-500 mt-0.5">Private to you — not shared with any applicant or tenant.</p>
                        </div>
                        <button type="button" @click="openUploadModal" class="primaryButton whitespace-nowrap">
                            <font-awesome-icon :icon="faUpload" class="mr-2" />
                            Upload Document
                        </button>
                    </div>

                    <div v-if="generalDocuments.length === 0" class="px-6 py-8 text-center text-gray-500">
                        <font-awesome-icon :icon="faFolderOpen" class="text-3xl text-gray-300 mb-2" />
                        <p class="text-sm">No general documents yet.</p>
                    </div>

                    <ul v-else class="divide-y divide-gray-100">
                        <li
                            v-for="doc in generalDocuments"
                            :key="doc.key"
                            class="px-6 py-4 flex items-start justify-between gap-4 hover:bg-gray-50"
                        >
                            <div class="flex items-start gap-3 min-w-0">
                                <div class="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <font-awesome-icon :icon="faFileLines" class="text-gray-500" />
                                </div>
                                <div class="min-w-0">
                                    <button
                                        type="button"
                                        @click="downloadGeneralDocument(doc)"
                                        class="text-sm font-medium text-purple-700 hover:text-purple-900 underline truncate text-left"
                                    >
                                        {{ doc.label }}
                                    </button>
                                    <p class="text-xs text-gray-500 truncate">{{ doc.file_name }}</p>
                                    <p v-if="doc.property_address" class="text-xs text-gray-500 truncate">From {{ doc.property_address }}</p>
                                    <p class="text-xs text-gray-400 mt-0.5">Uploaded {{ doc.uploaded_at }}</p>
                                </div>
                            </div>
                            <div class="flex items-center gap-2 whitespace-nowrap">
                                <button
                                    type="button"
                                    @click="downloadGeneralDocument(doc)"
                                    class="text-xs text-gray-600 hover:text-purple-700 font-medium px-3 py-1.5 border border-gray-300 rounded-md hover:border-purple-300 transition-colors"
                                >
                                    Download
                                </button>
                                <button
                                    v-if="doc.can_delete"
                                    type="button"
                                    @click="deleteGeneralDocument(doc)"
                                    class="text-xs text-red-600 hover:text-red-800 font-medium px-3 py-1.5 border border-red-300 rounded-md hover:border-red-400 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </li>
                    </ul>
                </div>

                <h2 class="text-sm font-semibold uppercase tracking-wide text-gray-500 pt-2">Shared with tenants</h2>

                <div v-if="properties.length === 0" class="bg-white rounded-lg shadow p-10 text-center">
                    <font-awesome-icon :icon="faFolderOpen" class="text-4xl text-gray-300 mb-3" />
                    <h3 class="text-lg font-medium text-gray-800">No shared documents yet</h3>
                    <p class="text-sm text-gray-500 mt-1">
                        Upload documents from a property's Documents tab and grant tenants access. Everything you share will be listed here across all your properties.
                    </p>
                </div>

                <div
                    v-for="group in properties"
                    :key="group.property_id"
                    class="bg-white rounded-lg shadow overflow-hidden"
                >
                    <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                        <h3 class="text-base font-semibold text-gray-800 truncate">
                            {{ group.address || 'Property' }}
                        </h3>
                        <Link
                            :href="route('properties.show', { property: group.property_id, tab: 'documents' })"
                            class="text-xs font-semibold px-3 py-1.5 rounded-md bg-gray-700 text-white hover:bg-gray-800 transition-colors whitespace-nowrap"
                        >
                            Manage access
                        </Link>
                    </div>

                    <ul class="divide-y divide-gray-100">
                        <li
                            v-for="doc in group.documents"
                            :key="doc.id"
                            class="px-6 py-4 flex items-start justify-between gap-4 hover:bg-gray-50"
                        >
                            <div class="flex items-start gap-3 min-w-0">
                                <div class="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <font-awesome-icon :icon="faFileLines" class="text-purple-600" />
                                </div>
                                <div class="min-w-0">
                                    <button
                                        type="button"
                                        @click="downloadDocument(doc)"
                                        class="text-sm font-medium text-purple-700 hover:text-purple-900 underline truncate text-left"
                                    >
                                        {{ doc.title || doc.file_name }}
                                    </button>
                                    <p v-if="doc.title" class="text-xs text-gray-500 truncate">{{ doc.file_name }}</p>
                                    <p class="text-xs text-gray-400 mt-0.5">Uploaded {{ doc.uploaded_at }}</p>
                                    <p
                                        class="text-xs mt-1"
                                        :class="doc.shared_with.length ? 'text-gray-600' : 'text-amber-600'"
                                    >
                                        <template v-if="doc.shared_with.length">
                                            Shared with: {{ doc.shared_with.map(u => u.name || u.email).join(', ') }}
                                        </template>
                                        <template v-else>
                                            Not shared with anyone yet
                                        </template>
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                @click="downloadDocument(doc)"
                                class="text-xs text-gray-600 hover:text-purple-700 font-medium px-3 py-1.5 border border-gray-300 rounded-md hover:border-purple-300 transition-colors whitespace-nowrap"
                            >
                                Download
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Upload General Document Modal -->
        <ModalJetstream :show="showUploadModal" :max-width="'lg'">
            <div class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h2 class="text-xl font-bold text-gray-800">Upload Document</h2>
                    <button type="button" @click="closeUploadModal" class="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div class="space-y-4">
                    <p class="text-sm text-gray-600">
                        These documents are private to you and are never shared with applicants or tenants.
                    </p>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Title <span class="text-gray-400">(optional)</span></label>
                        <input
                            v-model="uploadTitle"
                            type="text"
                            maxlength="255"
                            placeholder="e.g. Business License"
                            class="block w-full text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                        <p class="text-xs text-gray-500 mt-1">Defaults to the file name if left blank.</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">File</label>
                        <input
                            ref="fileInput"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            @change="handleFileChange"
                            class="block w-full text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm p-2"
                        >
                        <p class="text-xs text-gray-500 mt-1">PDF, JPG, PNG, DOC, DOCX. Max 10MB.</p>
                    </div>

                    <p v-if="uploadError" class="text-xs text-red-600 bg-red-50 p-2 rounded">{{ uploadError }}</p>

                    <div class="flex justify-end gap-3 mt-4">
                        <button type="button" @click="closeUploadModal" class="closeButton">Cancel</button>
                        <button
                            type="button"
                            @click="uploadFile"
                            :disabled="!selectedFile || uploading"
                            class="primaryButton"
                            :class="{ 'opacity-25': !selectedFile || uploading }"
                        >
                            {{ uploading ? 'Uploading...' : 'Upload' }}
                        </button>
                    </div>
                </div>
            </div>
        </ModalJetstream>
    </TailwindLayout>
</template>

<script setup>
import { appUrl } from '@/appUrl'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { Head, Link, router } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import axios from 'axios'
import ModalJetstream from '@/Jetstream/Modal.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFolderOpen, faFileLines, faUpload } from '@fortawesome/pro-light-svg-icons'

const props = defineProps({
    properties: { type: Array, default: () => [] },
    general_documents: { type: Array, default: () => [] },
})

const generalDocuments = computed(() => props.general_documents || [])

const downloadDocument = async (doc) => {
    try {
        const { data } = await axios.get(`/properties/${doc.property_id}/documents/${doc.id}/download`)
        if (data?.url) {
            window.open(data.url, '_blank')
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Failed to generate download link.')
    }
}

// General (private) documents
const showUploadModal = ref(false)
const uploading = ref(false)
const uploadError = ref(null)
const uploadTitle = ref('')
const selectedFile = ref(null)
const fileInput = ref(null)

const openUploadModal = () => {
    showUploadModal.value = true
}

const handleFileChange = (event) => {
    selectedFile.value = event.target.files[0] || null
    uploadError.value = null
}

const closeUploadModal = () => {
    showUploadModal.value = false
    selectedFile.value = null
    uploadTitle.value = ''
    uploadError.value = null
    if (fileInput.value) fileInput.value.value = ''
}

const uploadFile = async () => {
    if (!selectedFile.value) return
    uploading.value = true
    uploadError.value = null

    const formData = new FormData()
    formData.append('file', selectedFile.value)
    if (uploadTitle.value.trim()) formData.append('title', uploadTitle.value.trim())

    try {
        await axios.post('/user-documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        closeUploadModal()
        router.reload({ only: ['general_documents'], preserveScroll: true, preserveState: true })
    } catch (error) {
        uploadError.value = error.response?.data?.errors?.file?.[0]
            || error.response?.data?.errors?.title?.[0]
            || error.response?.data?.message
            || 'Upload failed. Please try again.'
    } finally {
        uploading.value = false
    }
}

const downloadGeneralDocument = async (doc) => {
    const url = doc.source === 'property'
        ? appUrl(`/properties/${doc.property_id}/documents/${doc.id}/download`)
        : `/user-documents/${doc.id}/download`
    try {
        const { data } = await axios.get(url)
        if (data?.url) {
            window.open(data.url, '_blank')
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Failed to generate download link.')
    }
}

const deleteGeneralDocument = async (doc) => {
    if (!window.confirm(`Delete ${doc.title || doc.file_name}? This cannot be undone.`)) return
    try {
        await axios.delete(`/user-documents/${doc.id}`)
        router.reload({ only: ['general_documents'], preserveScroll: true, preserveState: true })
    } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete document.')
    }
}
</script>
